import { useMutation } from '@apollo/client'

import {
  SaveOauthToken,
  SaveOauthTokenMutation,
  SaveOauthTokenMutationVariables,
  SendVerificationEmail,
  SendVerificationEmailMutation,
  SendVerificationEmailMutationVariables,
  VerifyCode,
  VerifyCodeMutation,
  VerifyCodeMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useAuth, useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getCredential,
  getMailInput,
  removeCredential,
  removeMailInput,
} from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { updateEmailSocial } = useAuth()
  const { t } = useTranslation('mailAuth')
  const { router, routerPages, generatePath } = useRouter()
  const { showError } = useMessage()

  const [sendVerifyEmail, { loading: verifyEmailSending }] = useMutation<
    SendVerificationEmailMutation,
    SendVerificationEmailMutationVariables
  >(SendVerificationEmail)
  const [saveOauthToken] = useMutation<
    SaveOauthTokenMutation,
    SaveOauthTokenMutationVariables
  >(SaveOauthToken)

  const [verifyCode] = useMutation<
    VerifyCodeMutation,
    VerifyCodeMutationVariables
  >(VerifyCode)

  return {
    state: {
      loading: verifyEmailSending,
    },
    actions: {
      async onResendAuthMail() {
        const { value } = await getMailInput()
        await sendVerifyEmail({ variables: { email: value ?? '' } })
      },
      async onSubmitMailAuth({ code }) {
        const { value } = await getMailInput()
        const { data } = await verifyCode({
          variables: { code, email: value ?? '' },
        })

        if (!data?.verifyCode.verified) throw new Error(t('verifyFailed'))

        await updateEmailSocial(value ?? '')
          .then(async () => {
            const credential = await getCredential()

            if (!credential || !Object.keys(credential).length) return

            await saveOauthToken({
              variables: {
                inputs: credential,
              },
            })
          })
          .catch(e => {
            showError(e)
          })
        await removeMailInput()
        await removeCredential()

        router.push(
          config?.artistId
            ? generatePath(routerPages.ArtistTop, {
                artistId: config.artistId,
              })
            : routerPages.GlobalHome
        )
      },
    },
  }
})
